/**
 * Analytics Context Provider
 * 
 * Provides analytics functionality throughout the React application
 */

'use client';

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  EventType, 
  EEGScenario, 
  ANALYTICS_ENDPOINTS, 
  TRACKING_EVENTS, 
  MUNICIPALITY_STEPS,
  INTERACTION_ACTIONS,
  CONVERSION_TYPES
} from '@/services/analytics';
// TestMode removed - now handled by URL params in API routes

interface AnalyticsContextType {
  sessionId: string | null;
  funnelType: 'municipality' | 'social_media' | 'email' | null;
  funnelId: string | null;
  isInitialized: boolean;
  
  // Session management
  initializeSession: (type: 'municipality' | 'social_media', params: any) => Promise<void>;
  
  // Event tracking
  trackEvent: (eventType: EventType, stepId: string, data?: Record<string, any>) => Promise<void>;
  trackStepEnter: (stepId: string, data?: Record<string, any>) => Promise<void>;
  trackStepComplete: (stepId: string, data?: Record<string, any>) => Promise<void>;
  trackInteraction: (stepId: string, action: string, data?: Record<string, any>) => Promise<void>;
  trackConversion: (conversionType: string, data?: Record<string, any>) => Promise<void>;
  
  // Municipality-specific tracking
  trackScenarioChange: (fromScenario: EEGScenario, toScenario: EEGScenario) => Promise<void>;
  trackParameterAdjustment: (parameter: string, oldValue: any, newValue: any) => Promise<void>;
  trackCallbackRequest: (callbackData: Record<string, any>) => Promise<void>;
  
  // Utility functions
  getSessionInfo: () => Promise<any>;
  isTrackingEnabled: () => boolean;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
  initialToken?: string;
  initialMunicipalityData?: any;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ 
  children, 
  initialToken,
  initialMunicipalityData 
}) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [funnelType, setFunnelType] = useState<'municipality' | 'social_media' | 'email' | null>(null);
  const [funnelId, setFunnelId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [trackingEnabled, setTrackingEnabled] = useState(true);

  // Initialize session on mount if initial data is provided
  useEffect(() => {
    if (initialToken && initialMunicipalityData && !isInitialized) {
      initializeSession('municipality', {
        token: initialToken,
        municipalityId: initialMunicipalityData.gemeinde_id,
        municipalityName: initialMunicipalityData.gemeinde_name || initialMunicipalityData.name
      });
    }
  }, [initialToken, initialMunicipalityData, isInitialized]);

  // Check for tracking opt-out
  useEffect(() => {
    const checkTrackingStatus = () => {
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const optOut = urlParams.get('no_tracking') === 'true' || 
                       localStorage.getItem('analytics_opt_out') === 'true';
        
        setTrackingEnabled(!optOut);
        
        if (optOut) {
          console.log('[Analytics Context] Tracking opt-out active - funnel tracking disabled');
        }
      }
    };

    checkTrackingStatus();
    
    // Listen for storage changes to update tracking status
    const handleStorageChange = () => {
      checkTrackingStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const initializeSession = async (type: 'municipality' | 'social_media', params: any) => {
    if (!trackingEnabled) {
      console.log('[Analytics] Tracking disabled, skipping session initialization');
      return;
    }

    console.log('[Analytics] Initializing session:', { type, params, trackingEnabled });

    try {
      // Pass test mode status via URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const testParam = urlParams.get('test') === 'true' ? '?test=true' : '';
      
      const response = await fetch(`${ANALYTICS_ENDPOINTS.SESSION}${testParam}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type,
          ...params
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setSessionId(result.data.sessionId);
        setFunnelType(type);
        // Use the funnelId returned from the backend instead of generating it here
        setFunnelId(result.data.funnelId);
        setIsInitialized(true);
        
        console.log('[Analytics] Session initialized:', result.data);
      } else {
        console.error('[Analytics] Failed to initialize session:', result.error);
      }
    } catch (error) {
      console.error('[Analytics] Error initializing session:', error);
    }
  };

  const trackEvent = async (eventType: EventType, stepId: string, data: Record<string, any> = {}) => {
    if (!trackingEnabled || !sessionId || !funnelType || !funnelId) {
      console.log('[Analytics] Skipping event tracking:', {
        trackingEnabled,
        sessionId: sessionId ? 'set' : 'missing',
        funnelType: funnelType || 'missing',
        funnelId: funnelId || 'missing'
      });
      return;
    }

    console.log('[Analytics] Tracking event:', { eventType, stepId, sessionId, funnelId });

    try {
      // Pass test mode status via URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const testParam = urlParams.get('test') === 'true' ? '?test=true' : '';
      
      const response = await fetch(`${ANALYTICS_ENDPOINTS.TRACK}${testParam}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          sourceType: funnelType === 'municipality' ? 'municipality' : 
                     funnelType === 'social_media' ? 'social_media' : 'email',
          funnelId,
          eventType,
          stepId,
          data
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        console.error('[Analytics] Failed to track event:', result.error);
      }
    } catch (error) {
      console.error('[Analytics] Error tracking event:', error);
    }
  };

  const trackStepEnter = async (stepId: string, data: Record<string, any> = {}) => {
    await trackEvent(TRACKING_EVENTS.STEP_ENTER as EventType, stepId, {
      ...data,
      timestamp: new Date().toISOString()
    });
  };

  const trackStepComplete = async (stepId: string, data: Record<string, any> = {}) => {
    await trackEvent(TRACKING_EVENTS.STEP_COMPLETE as EventType, stepId, {
      ...data,
      timestamp: new Date().toISOString()
    });
  };

  const trackInteraction = async (stepId: string, action: string, data: Record<string, any> = {}) => {
    await trackEvent(TRACKING_EVENTS.INTERACTION as EventType, stepId, {
      action,
      ...data,
      timestamp: new Date().toISOString()
    });
  };

  const trackConversion = async (conversionType: string, data: Record<string, any> = {}) => {
    await trackEvent(TRACKING_EVENTS.CONVERSION as EventType, MUNICIPALITY_STEPS.CALLBACK_REQUEST, {
      conversion_type: conversionType,
      ...data,
      timestamp: new Date().toISOString()
    });
  };

  const trackScenarioChange = async (fromScenario: EEGScenario, toScenario: EEGScenario) => {
    if (!trackingEnabled || !sessionId || !funnelId) return;

    try {
      // Pass test mode status via URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const testParam = urlParams.get('test') === 'true' ? '?test=true' : '';
      
      const response = await fetch(`${ANALYTICS_ENDPOINTS.TRACK}${testParam}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          sourceType: funnelType === 'municipality' ? 'municipality' : 
                     funnelType === 'social_media' ? 'social_media' : 'email',
          funnelId,
          fromScenario,
          toScenario
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (!result.success) {
        console.error('[Analytics] Failed to track scenario change:', result.error);
      }
    } catch (error) {
      console.error('[Analytics] Error tracking scenario change:', error);
    }
  };

  const trackParameterAdjustment = async (parameter: string, oldValue: any, newValue: any) => {
    await trackInteraction(MUNICIPALITY_STEPS.PARAMETER_ADJUSTMENT, INTERACTION_ACTIONS.PARAMETER_CHANGE, {
      parameter,
      old_value: oldValue,
      new_value: newValue
    });
  };

  const trackCallbackRequest = async (callbackData: Record<string, any>) => {
    await trackConversion(CONVERSION_TYPES.CALLBACK, callbackData);
  };

  const getSessionInfo = async () => {
    if (!sessionId || !funnelType || !funnelId) return null;

    try {
      const response = await fetch(
        `${ANALYTICS_ENDPOINTS.SESSION}?sessionId=${sessionId}&sourceType=${funnelType === 'municipality' ? 'municipality' : 
                     funnelType === 'social_media' ? 'social_media' : 'email'}&funnelId=${funnelId}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result.success ? result.data : null;
    } catch (error) {
      console.error('[Analytics] Error getting session info:', error);
      return null;
    }
  };

  const isTrackingEnabled = () => trackingEnabled;

  const value: AnalyticsContextType = {
    sessionId,
    funnelType,
    funnelId,
    isInitialized,
    initializeSession,
    trackEvent,
    trackStepEnter,
    trackStepComplete,
    trackInteraction,
    trackConversion,
    trackScenarioChange,
    trackParameterAdjustment,
    trackCallbackRequest,
    getSessionInfo,
    isTrackingEnabled
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

// Optional: Analytics debugging component
export const AnalyticsDebugger: React.FC = () => {
  const analytics = useAnalytics();
  const [isTestMode, setIsTestMode] = useState(false);
  
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  // Check test mode from URL
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      setIsTestMode(urlParams.get('test') === 'true');
    }
  }, []);

  const toggleTestMode = () => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (isTestMode) {
        url.searchParams.delete('test');
      } else {
        url.searchParams.set('test', 'true');
      }
      window.location.href = url.toString();
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 10,
      right: 10,
      background: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      padding: '10px',
      borderRadius: '4px',
      fontSize: '12px',
      zIndex: 9999,
      minWidth: '200px'
    }}>
      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Analytics Debug</div>
      <div>Session: {analytics.sessionId ? analytics.sessionId.substring(0, 8) + '...' : 'None'}</div>
      <div>Type: {analytics.funnelType || 'None'}</div>
      <div>ID: {analytics.funnelId || 'None'}</div>
      <div>Initialized: {analytics.isInitialized ? 'Yes' : 'No'}</div>
      <div>Tracking: {analytics.isTrackingEnabled() ? 'Enabled' : 'Disabled'}</div>
      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #444' }}>
        <div style={{ fontWeight: 'bold' }}>Test Mode</div>
        <div>Status: {isTestMode ? 'Active (via URL)' : 'Inactive'}</div>
        <button 
          onClick={toggleTestMode}
          style={{
            marginTop: '4px',
            padding: '4px 8px',
            fontSize: '10px',
            background: isTestMode ? '#e74c3c' : '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: '2px',
            cursor: 'pointer'
          }}
        >
          {isTestMode ? 'Disable Test Mode' : 'Enable Test Mode'}
        </button>
      </div>
    </div>
  );
};